import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details'];

  /** @type {HTMLElement | null} */
  #movedDrawer = null;
  /** @type {HTMLElement | null} */
  #movedBackdrop = null;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
    
    // Move drawer and backdrop to body to escape stacking contexts
    this.#moveToBody();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    
    // Clean up moved elements
    if (this.#movedDrawer) {
      this.#movedDrawer.remove();
    }
    if (this.#movedBackdrop) {
      this.#movedBackdrop.remove();
    }
  }
  
  /**
   * Move drawer and backdrop to body to escape any ancestor stacking contexts
   */
  #moveToBody() {
    const drawer = this.querySelector('.menu-drawer');
    const backdrop = this.querySelector('.menu-drawer__backdrop');
    
    if (drawer) {
      this.#movedDrawer = drawer;
      document.body.appendChild(drawer);
      
      // Use event delegation for all interactive elements
      drawer.addEventListener('click', (e) => {
        const target = e.target;
        
        // Close button
        if (target.closest('.menu-drawer__close-button')) {
          e.preventDefault();
          this.close();
          return;
        }
        
        // Back button
        if (target.closest('.menu-drawer__back-button')) {
          e.preventDefault();
          const details = target.closest('details');
          if (details) {
            this.#close(details);
          }
          return;
        }
        
        // Submenu summary (open submenu)
        const summary = target.closest('details.menu-drawer__menu-container > summary');
        if (summary) {
          e.preventDefault();
          const details = summary.closest('details');
          if (details) {
            details.setAttribute('open', '');
            this.open({ target: summary });
          }
          return;
        }
      });
      
      // Handle Escape key in moved drawer
      drawer.addEventListener('keyup', (e) => {
        if (e.key === 'Escape') {
          const details = e.target.closest('details');
          if (details) {
            this.#close(details);
          } else {
            this.close();
          }
        }
      });
    }
    
    if (backdrop) {
      this.#movedBackdrop = backdrop;
      document.body.appendChild(backdrop);
      
      // Close on backdrop click
      backdrop.addEventListener('click', () => this.close());
    }
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    // Look for details in the event target (could be in moved drawer)
    const details = event.target.closest('details');
    if (details) return details;
    
    return this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {Event} [event]
   */
  open(event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');
    requestAnimationFrame(() => {
      details.classList.add('menu-open');
      // Also toggle class on moved elements for the main drawer
      if (details === this.refs.details) {
        this.#movedDrawer?.classList.add('menu-open');
        this.#movedBackdrop?.classList.add('menu-open');
      }
      
      // Always scroll the main drawer container to top when opening any menu
      if (this.#movedDrawer) {
        this.#movedDrawer.scrollTop = 0;
      }
      
      // Also scroll submenu to top when opening second tier
      const submenu = details.querySelector('.menu-drawer__submenu');
      if (submenu) {
        submenu.scrollTop = 0;
      }
    });

    trapFocus(this.#movedDrawer || details);
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    
    // Also toggle class on moved elements for the main drawer
    if (details === this.refs.details) {
      this.#movedDrawer?.classList.remove('menu-open');
      this.#movedBackdrop?.classList.remove('menu-open');
    }

    onAnimationEnd(this.#movedDrawer || details, () => {
      reset(details);

      if (details === this.refs.details) {
        removeTrapFocus();
        // Query from moved drawer since it's now in body
        const openDetails = this.#movedDrawer?.querySelectorAll('details[open]') || [];
        openDetails.forEach(reset);
      } else {
        trapFocus(this.#movedDrawer || this.refs.details);
      }
    });
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    /**
     * @param {AnimationEvent} event
     */
    function removeWillChangeOnAnimationEnd(event) {
      const target = event.target;
      if (target && target instanceof HTMLElement) {
        target.style.setProperty('will-change', 'unset');
        target.removeEventListener('animationend', removeWillChangeOnAnimationEnd);
      }
    }
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
